<script setup>
import "@/assets/new-style/all.css";
import "@/assets/new-style/uikit.css";
import LoaderElement from '@/components/general/LoaderElement.vue';
import { useMetrics } from "@/composables/use.metrics";
import { getEventSource } from "@/helpers/getEventSource";
import { errorHandler } from "@/lib/errorHandler";
import locations from "@/lib/locations.json";
import { targetActionsMap } from "@/lib/sendYmParams";
import { useIntersectionObserver } from '@vueuse/core';
import dayjs from "dayjs";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/vue";
import { computed, defineEmits, defineProps, onBeforeMount, onBeforeUnmount, ref, shallowRef, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
// import BannerComponent from './BannerComponent.vue';


const store = useStore();
const router = useRouter();

const props = defineProps(["format"]);
const emits = defineEmits(["links", 'landingEvents', 'open-map']);

const eventsList = ref([]);

const notMos = computed(() => {
  if (location.hostname.includes('test')) {
    return false;
  }

  return location.hostname.includes('metamsk.mos.ru') || location.hostname.includes('metamsk.ru')
});

const banner = computed(() => {
  const event = eventsList.value?.find(({ eventPlace }) => eventPlace === locations.russian.place);

  return event;
});

const currentEventType = ref("");

const user = computed(() => store.getters.userData);

const {hit, getMetricsEventType, hitType} = useMetrics();

// const eventTypes = {
//   "": "Все события",
//   lecture: "Лекции",
//   excursion: "Экскурсии",
//   broadcast: "Трансляции",
//   exhibition: "Выставки",
//   record: "Записи",
//   dedicatedTour: "Живые экскурсии",
//   recordedTour: "Экскурсии по ВДНХ",
//   vdnh_excursion: "Прогулка по ВДНХ",
// };

const eventList = computed(() => !notMos.value ? eventsList.value : eventsList.value.filter(({ eventPlace }) => eventPlace !== locations.russian.place));

const filteredEvents = computed(() => {
    return eventList.value?.filter?.((event) =>
      !currentEventType.value ? true : event?.type === currentEventType.value,
    ) ?? [];
  }
);


async function bookedEvent(event) {
  if (props.format === "landing") {
    return await router.push({ name: "mainPage" });
  }

  if (event?.type === "record") {
    if (!event?.translationUrl) {
      return errorHandler({ name: "videoRecordingDoesNotExist" });
    }
    const { state } = await store.dispatch(
      "checkVideoRecordingAvailable",
      event.translationUrl,
    );
    if (!state) {
      errorHandler({ name: "videoRecordingDoesNotExist" });
    } else {
      await router.push({ name: "videoPage", params: { id: event.id } });
    }
    return;
  }
  if (event?.type === "broadcast") {
    const el = document.createElement("a");

    el.href = event?.eventPlace;
    el.target = "_blank";
    el.click();

    return;
  }


  const metricsEventType = getMetricsEventType(event);

  if (metricsEventType) {
    await hit(metricsEventType, hitType.SELECT_EVENT, event);
  }

  if (getEventStatus(event) !== "ok") {
    errorHandler({ name: "eventStatus", status: getEventStatus(event) });
    return;
  }

  if (event?.type === "lecture" && user.value.role !== "lecture") {
    if (!event?.placement) {
      return router.push({
        name: "pickPlacement",
        params: { eventId: event?.id },
      });
    } else {
      sessionStorage.setItem("eventPlacement", JSON.stringify(event.placement));
    }
  }

  // if (user.value.role !== "guide") {
  if (event.eventSlots && event.eventSlots?.ticketQuantityOnDay) {
    if (event.eventPlace === locations.russian.place) {
      const { origin } = window.location;
      const url = new URL(`/cabinet/date-select/${event.id}`, origin).toString();

      return window.open(url, '_self');
    }

    return router.push({
      name: "dateSelect",
      params: {
        id: event.id,
      },
    });
  }

  if (event.participantsCount >= event.participantsLimit) {
    return errorHandler({ name: "participantsLimit" });
  }

  if (["dedicatedTour", "excursion"]?.includes(event?.type)) {
    return await router.push({ name: "lobbyPage", params: { id: event.id } });
  }
  // }

  sessionStorage.setItem("eventId", event.id);
  sessionStorage.setItem("internalId", event.internalId);
  sessionStorage.setItem("eventType", event.type);
  return await router.push({ name: "gamePage" });

  // await store.dispatch('bookedEvent', {
  //   eventId: event.id,
  //   isLector: user.value.role === 'lecture',
  // }).then((status) => {
  //   if (status === 200) {
  //     const index = eventsList.value?.findIndex((item) => item?.id === event.id);
  //     if (index > -1) {
  //       eventsList.value[index].isSignedUp = true;
  //     }
  //   }
  // });
}

function bookEventByButton(event) {
  const actionNumber = router.currentRoute.value.name === 'mainPage' ? 29 : 6;
  targetActionsMap.get(actionNumber)(event.title);
  bookedEvent(event);
}

function getEventStatus(event) {
  if (["record", "broadcast"].includes(event?.type)) return "ok";

  if (dayjs().unix() > dayjs(event.eventDateEnd).unix()) return "end";

  if (dayjs().unix() < dayjs(event.eventDateStart).unix()) return "notStarted";

  return "ok";
}

const loading = shallowRef(false);

async function init() {
  loading.value = true;

  switch (props.format) {
    case "landing":
      store
        .dispatch("fetchCalendarEventsRecommendation", {
          type: currentEventType.value,
          page: 1,
          limit: 100,
        })
        .then((data) => {
          eventsList.value = data;
          emits('landingEvents', data)
        }).finally(() => (loading.value = false));
      break;

    case "today":
      store.dispatch("fetchCalendarEventsRecommendation").then((data) => {
        eventsList.value = data;
      }).finally(() => (loading.value = false));
      break;

    case "all":
      store
        .dispatch("fetchCalendarEventsRecommendation", {
          type: currentEventType.value,
          page: 1,
          limit: 100,
        })
        .then(() => {
          eventsList.value = store.getters.calendarEvents;
        }).finally(() => (loading.value = false));
      break;
  }
}

onBeforeMount(init);

function createDateSelectPageLink(id) {
  if (!id?.length) return '#';
  return `/cabinet/date-select/${id}`;
}

watch(filteredEvents, value => {
  if (value) {
    const vdnhId = value.find(({ type, eventPlace }) => type === "vdnh_excursion" && eventPlace !== locations.russian.place)?.id ?? 0;
    const aveleksId = value.find(({ type }) => type !== "vdnh_excursion")?.id ?? 0;
    const atomId = value.find(({ eventPlace, title }) => eventPlace === 'atom' || title.toLocaleLowerCase().includes('атом'))?.id ?? 0;
    const greenChronicsId = value.find(({ eventPlace, title }) => eventPlace === 'allPlace' || title.toLocaleLowerCase().includes('хроники'))?.id ?? 0;

    emits('links', {
      vdnh: createDateSelectPageLink(vdnhId),
      aveleks: createDateSelectPageLink(aveleksId),
      russia: createDateSelectPageLink(banner.value?.id),
      greenChronics: createDateSelectPageLink(greenChronicsId),
      atom: createDateSelectPageLink(atomId)
    })
  }
});

function loadAltPreview(event) {
  event.target.src = require("@/assets/new-icons/landing/test-data/test-1.png");
}

const target17 = ref();

const { stop } = useIntersectionObserver(target17, ([{ isIntersecting }]) => {
  if (isIntersecting && router.currentRoute.value.name !== 'mainPage') {
    stop();
    window?.ym?.(97993667,'reachGoal','all_events')
    targetActionsMap.get(17)();
  }
});

onBeforeUnmount(stop)


function getEventsByKey(key) {
  return eventsList.value?.filter(({ type }) => (!key?.length ? true : type === key))
}

// function onChangeEventType(type) {
//   if (type === currentEventType.value) {
//     return;
//   }

//   const actionNumber = router.currentRoute.value.name === 'mainPage' ? 30 : 7;

//   currentEventType.value = type;
//   targetActionsMap.get(actionNumber)(type.length ? eventTypes[type] : 'Все события');
// }


function getEventDescription(description) {
  if (!description.toLowerCase().includes('международная') && !description.toLowerCase().includes('россия')) {
    return description;
  }

  return 'Посетите уникальные экспозиции международной выставки-форума «России» в метавселенной. Выбирайте удобное время и отправляйтесь на прогулку!'
}
</script>

<template>
  <Transition name="fade" mode="out-in">
    <LoaderElement v-if="loading" :page="false" key="loading" />
    <section v-else class="section events" key="content">
      <div class="events-filling container" v-if="eventsList?.length">
        <h2 ref="target17" class="events__title">
          Актуальное
        </h2>
        <!-- <BannerComponent :banner="banner" /> -->
        <!-- <p class="events__subtitle">Тип экскурсии:</p> -->
        <div class="events-caption">
          <!-- <div class="events-type">
            <button v-for="(item, key) in eventTypes" :key="key" class="events-type__btn trans" v-show="getEventsByKey(key).length" :class="{ 'events-type__btn--selected': currentEventType === key }" @click="onChangeEventType(key)">
              {{ item }}
              <span class="events-type__number">{{ getEventsByKey(key).length }}</span>
            </button>
          </div> -->
          <div class="events-nav">
            <button class="events-nav__btn events-nav__btn--prev">
              <img src="@/assets/new-icons/ChevronLeft.svg" alt="Прошлый слайд" class="events-nav__icon" />
            </button>
            <button class="events-nav__btn events-nav__btn--next">
              <img src="@/assets/new-icons/ChevronRight.svg" alt="Следующий слайд" class="events-nav__icon" />
            </button>
          </div>
        </div>
        <swiper :navigation="{
          nextEl: '.events-nav__btn--next',
          prevEl: '.events-nav__btn--prev',
        }" :space-between="20" :grab-cursor="true" :slides-per-view="2" :modules="[Navigation]" class="events__swiper">
          <swiper-slide v-for="item in getEventsByKey(currentEventType)" :key="item.id" class="events-slide" @click="bookedEvent(item)">
            <div class="events-slide-photo">
              <img v-if="item?.title === 'Хроники Победы'" src="@/assets/chronicles-bg.png" alt="" @error="loadAltPreview" class="events-slide-photo__img" />
              <img v-else :src="getEventSource(item)" alt="" @error="loadAltPreview" class="events-slide-photo__img" />
            </div>
            <div class="events-content">
              <p class="events-slide__title line-limit-3">{{ item.title }}</p>
              <p class="events-slide__text line-limit-4">
                {{ getEventDescription(item.description) }}
              </p>
              <div class="events-slide-footer">
                <div class="footer-actions">
                  <button class="link link--small" @click.stop="bookEventByButton(item)">
                    {{
                      format === "landing"
                        ? "Записаться"
                        : getEventStatus(item) === "ok"
                          ? ["broadcast", "exhibition", "record"].includes(item.type)
                            ? "Перейти"
                            : "Записаться"
                          : getEventStatus(item) === "end"
                            ? item.type === "record"
                              ? "Перейти"
                              : "Мероприятие закончилось"
                            : getEventStatus(item) === "notStarted"
                              ? item.type === "record"
                                ? "Перейти"
                                : "Мероприятие не началось"
                              : ""
                    }}
                  </button>
                  <button
                    v-if="item.eventPlace === 'russia'"
                    id="russia"
                    class="link link--small"
                    @click.stop.prevent="$emit('open-map')"
                  >
                    Подробнее
                  </button>
                </div>
                <div class="events-slide-info__elements">
                  <div class="events-slide-info__element">
                    <img src="@/assets/new-icons/landing/time.svg" alt="" class="events-slide-info__icon" />
                    <p class="events-slide-info__text">
                      {{ item?.eventSlots?.duration ?? 30 }} мин.
                    </p>
                  </div>
                  <div class="events-slide-info__separate"></div>
                  <div class="events-slide-info__element">
                    <img src="@/assets/icons/eye2.svg" alt="" class="events-slide-info__icon" />
                    <p class="events-slide-info__text">
                      {{ item.eventSlots?.ticketQuantityOnDay }} чел.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </swiper-slide>
        </swiper>
        <div class="events-list">
          <div class="events-slide" v-for="item in eventList" :key="item.id">
            <div class="events-slide-photo">
              <img v-if="item?.title === 'Хроники Победы'" src="@/assets/chronicles-bg.png" alt="" @error="loadAltPreview" class="events-slide-photo__img" />
              <img v-else :src="getEventSource(item)" alt="" @error="loadAltPreview" class="events-slide-photo__img" />
            </div>
            <div class="events-content">
              <p class="events-slide__title line-limit-2">{{ item.title }}</p>
              <p class="events-slide__text line-limit-4" v-if="item.description">
                {{ item.description }}
              </p>
              <div v-if="false" class="events-slide-info">
                <div class="events-slide-info__element">
                  <img src="@/assets/new-icons/landing/time.svg" alt="" class="events-slide-info__icon" />
                  <p class="events-slide-info__text">
                    {{ dayjs(item?.eventDateStart)?.format("DD MMMM hh:mm") }} -
                    {{ dayjs(item?.eventDateEnd)?.format("DD MMMM hh:mm") }}
                  </p>
                </div>
              </div>
              <button class="link link--small" @click.stop="bookEventByButton(item)">
                {{
                  format === "landing"
                    ? "Записаться"
                    : getEventStatus(item) === "ok"
                      ? ["broadcast", "exhibition", "record"].includes(item.type)
                        ? "Перейти"
                        : "Записаться"
                      : getEventStatus(item) === "end"
                        ? item.type === "record"
                          ? "Перейти"
                          : "Мероприятие закончилось"
                        : getEventStatus(item) === "notStarted"
                          ? item.type === "record"
                            ? "Перейти"
                            : "Мероприятие не началось"
                          : ""
                }}
              </button>
              <!-- <button class="link link--small" @click="bookedEvent(item)">
                {{
                  format === "landing"
                    ? "Записаться"
                    : getEventStatus(item) === "ok"
                      ? ["broadcast", "exhibition", "record"].includes(item.type)
                        ? "Перейти"
                        : "Перейти на локацию"
                      : getEventStatus(item) === "end"
                        ? item.type === "record"
                          ? "Перейти"
                          : "Мероприятие закончилось"
                        : getEventStatus(item) === "notStarted"
                          ? item.type === "record"
                            ? "Перейти"
                            : "Мероприятие не началось"
                          : ""
                }}
              </button> -->
            </div>
          </div>
        </div>
        <!-- <button class="show-more">Показать ещё</button> -->
      </div>
      <div class="no-results container" v-else>
        <h2 class="events__title">
          {{
            format === "today"
              ? "Мероприятия сегодня"
              : format === "all"
                ? "Все мероприятия"
                : ""
          }}
        </h2>
        <div class="no-results-filling">
          <img src="@/assets/new-icons/no-results-decoration.png" alt="" class="no-results__img" />
          <p class="no-results__text">
            К сожалению, места на сегодня закончились, но вы можете выбрать время в
            любой другой день
          </p>
        </div>
      </div>
    </section>
  </Transition>
</template>

<style lang="scss" scoped>
.events {
  padding-top: 2.75rem;

  &__title {
    font-weight: 500;
    font-size: 4rem;
    line-height: 112%;
    color: #0a0033;
  }

  &__subtitle {
    margin-top: 2rem;
    font-weight: 400;
    font-size: 1rem;
    line-height: 125%;
    color: #8f8f8f;
  }

  &-caption {
    margin-top: 1rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    max-width: 100%;
    width: 100%;
    justify-content: space-between;
  }

  &-content {
    padding: 0 1.25rem;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &-type {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;

    &__btn {
      padding: 0.875rem 1.25rem;
      display: flex;
      gap: 0.5rem;
      align-items: center;
      font-weight: 400;
      font-size: 1rem;
      color: #000;
      background: #f3f4f6;
      border-radius: 6rem;

      &:hover {
        color: #fff;
        background: #00288C;
        opacity: 1;
      }

      &--selected {
        color: #fff;
        background: #00288C;
      }
    }

    &__number {
      font-weight: 400;
      font-size: 0.875rem;
      line-height: 143%;
      border-radius: 5rem;
      padding: 0 0.25rem;
      min-width: 1.25rem;
      background: #fff;
      transition: all 0s ease;
      color: #000;
    }
  }

  &-nav {
    display: flex;
    align-items: center;
    gap: 1.5rem;

    &__btn {
      width: 1.5rem;
      height: 1.5rem;

      img {
        width: 100%;
        height: 100%;
      }
    }
  }

  &__swiper {
    margin-top: 1.5rem;
    display: inline-grid;
    max-width: 100%;
    width: 100%;
  }

  &-list {
    display: none;
  }

  &-slide {
    padding: 1rem 1rem 1.25rem 1rem;
    background: #f0f2f6;
    border-radius: 2.125rem;
    display: flex;
    flex-direction: column;

    &-photo {
      position: relative;
      padding-bottom: 63.69%;

      &__img {
        border-radius: 1.75rem;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        z-index: 0;
        object-fit: cover;
        object-position: center;
      }
    }

    &__title {
      margin-top: 1.25rem;
      font-weight: 500;
      font-size: 2rem;
      line-height: 100%;
    }

    &__text {
      margin-top: 1rem;
      font-weight: 400;
      font-size: 1.125rem;
      line-height: 133%;
      color: rgba(0, 0, 0, 0.8);
    }

    &-footer {
      padding-top: 1.5rem;
      margin-top: auto;
      display: flex;
      align-items: center;
      justify-content: space-between;


      .footer-actions {
        display: flex;
        align-items: center;
        gap: 10px;
      }
    }

    &-info {
      display: flex;
      align-items: center;
      gap: 1rem;

      &__elements {
        display: flex;
        align-items: center;
        gap: 0.7rem;
      }

      &__separate {
        width: 3px;
        height: 3px;
        border-radius: 50%;
        background-color: #cdcdcd;
      }

      &__element {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }

      &__icon {
        width: 1.25rem;
        height: 1.25rem;
      }

      &__text {
        font-size: 1rem;
        line-height: 87%;
        color: #828282;
      }
    }
  }

  @media (max-width: 1100px) {
    padding: 1.5rem 0 0 0;

    &__title {
      font-size: 2rem;
      line-height: 130%;
    }

    &__subtitle {
      font-weight: 400;
      font-size: 1rem;
    }

    &-type {
      &__btn {
        padding: 0.6875rem 1rem;
        gap: 0.375rem;
        font-size: 0.8rem;
        line-height: 130%;
      }

      &__number {
        font-size: 0.75rem;
        line-height: 100%;
        min-width: 1.25rem;
        padding: 0.25rem;
      }
    }

    &-content {
      padding: 0 0.5rem;
    }

    &-nav {
      display: none;
    }

    &__swiper {
      display: none;
    }

    &-list {
      margin-top: 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    &-slide {
      padding: 0.5rem 0.5rem 0.75rem 0.5rem;

      &-photo {
        &__img {
          border-radius: 1.75rem;
        }
      }

      &__title {
        margin-top: 1.375rem;
        font-size: 1.5rem;
        line-height: 130%;
      }

      &__text {
        margin-top: 0.5rem;
        margin-bottom: 0;
        font-weight: 400;
        font-size: 1rem;
        line-height: 140%;
      }

      &-info {
        margin-top: 1rem;

        &__icon {
          width: 1rem;
          height: 1rem;
        }

        &__text {
          font-size: 0.88rem;
          line-height: 130%;
        }

        &__element {
          padding: 0;
        }
      }

      .link {
        margin-top: 1.5rem;
        padding: 0.8125rem;
        max-width: 100%;
        width: 100%;
        justify-content: center;
        font-size: 1rem;
        border-radius: 12.5rem;
      }
    }

    &-footer {
      flex-direction: column-reverse;
    }
  }
}

// .show-more {
//   display: none;

//   @media (max-width: 1100px) {
//     margin-top: 1.5rem;
//     display: flex;
//     max-width: 100%;
//     width: 100%;
//     font-size: 1.12rem;
//     line-height: 133%;
//     padding: 1rem;
//     background: #f3f4f6;
//     border-radius: 12.5rem;
//     justify-content: center;
//   }
// }

.no-results {
  &-filling {
    margin-top: 2rem;
    gap: 2rem;
    padding: 1.25rem;
    background: #00288C;
    border-radius: 2rem;
    display: flex;
    align-items: center;
  }

  &__img {
    display: flex;
    flex-shrink: 0;
    width: 7.5rem;
    height: 7.5rem;
  }

  &__text {
    font-weight: 500;
    font-size: 2rem;
    line-height: 1.3;
    letter-spacing: 0.01em;
    color: #ffffff;
  }

  @media (max-width: 1100px) {
    &-filling {
      padding: 1rem;
      border-radius: 1.5rem;
    }

    &__img {
      display: none;
    }

    &__text {
      font-size: 1.25rem;
      line-height: 1.3;
    }
  }
}
</style>
